import { useNavigate } from 'hooks';
import { List } from 'components/list';
import { Link } from 'components/link';
import { useAppSelector } from 'store';
import { Messages, AppRoutes } from 'config';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { DeleteDialog } from 'components/delete-dialog';
import { formatPhone, formatPrice } from 'utils/helpers';
import { useRemoveMutation } from 'store/api/stock-orders';
import { formatDate, formatOptionalDate } from 'utils/date';
import { canEditCompletedLtl } from 'components/stock-ltl-edit/config';
import { isAuthCompanyUser, selectAuthUserRole } from 'store/slices/auth';
import {
  StockOrderStatus,
  StockOrderStatusLabel,
} from 'store/api/stock-orders/types';
import { LazyStockOrderShippingLtlEdit } from 'pages/inventory/stock-orders/shipping-info/lazy';

import * as Styles from './styles';
import { StockOrderDetailsProps } from './types';

const titles = ['Name', 'Count', 'Price', 'Total price'];
const splitTitles = ['Event', 'Percent', 'LTL share'];
const commonColProps = {
  col: 3,
  colTablet: 3,
  colMobile: 6,
};

export const StockOrderDetails = ({
  data,
  isOnlyRead,
  disableSticky,
}: StockOrderDetailsProps) => {
  const navigate = useNavigate();
  const [remove, mutation] = useRemoveMutation();
  const isCompanyOwner = useAppSelector(isAuthCompanyUser);

  const role = useAppSelector(selectAuthUserRole);

  const isDone = data?.status === StockOrderStatus.Done;
  const canEdit = !isOnlyRead && (!isDone || isCompanyOwner);
  // L4a: on a Done order the LTL Invoice # / Price stay editable for the
  // Company Owner and Administrative staff (hidden while Main lacks the route)
  const canEditLtl = !isOnlyRead && isDone && canEditCompletedLtl(role);

  return (
    <Styles.StyledGeneralInfo
      withoutPhoto
      withoutPadding={isOnlyRead}
      name={`Stock order ${data.id}`}
      disableSticky={isOnlyRead || disableSticky}
      onEdit={canEdit ? () => navigate('edit') : undefined}
      rightSlot={
        !isOnlyRead ? (
          <DeleteDialog
            id={data.id}
            action={remove}
            name="stock order"
            mutation={mutation}
            navigateUrl={AppRoutes.Inventory.StockOrders.Home}
            successMessage={`Stock order ${Messages.DELETED_SUCCESSFULLY}`}
          />
        ) : undefined
      }
      sections={[
        {
          title: 'General info',
          rows: [
            [
              {
                ...commonColProps,
                title: 'Supplier',
                value: data.supplier.name,
              },
              {
                ...commonColProps,
                title: 'Event',
                value: data.event?.name,
              },
              {
                ...commonColProps,
                title: 'Storage',
                value: data.storage?.name,
              },
              {
                ...commonColProps,
                title: 'Status',
                value: StockOrderStatusLabel[data.status],
              },
            ],
            [
              {
                ...commonColProps,
                title: 'Order Date',
                value: formatDate(data.date),
              },
              {
                ...commonColProps,
                title: 'Number',
                value: data.number,
              },
              {
                ...commonColProps,
                title: 'Work Order',
                value: data.workOrder,
              },
            ],
            [
              {
                ...commonColProps,
                title: 'Address',
                value: data.address,
              },
              {
                ...commonColProps,
                title: 'Contact Name',
                value: data.contactName,
              },
              {
                ...commonColProps,
                title: 'Phone',
                value: data?.phone ? formatPhone(data.phone) : null,
              },
              {
                ...commonColProps,
                title: 'Emergency Phone',
                value: data?.emergencyPhone
                  ? formatPhone(data.emergencyPhone)
                  : null,
              },
            ],
          ],
        },
        {
          title: 'Shipment Info',
          rightSlot: canEditLtl ? (
            // T-F07: the "Shipping & LTL" dialog (carrier, tracking #,
            // dates, LTL invoice # / price) through the L4a route
            <LazyStockOrderShippingLtlEdit id={data.id} />
          ) : undefined,
          rows: [
            [
              {
                col: 3,
                colTablet: 3,
                title: 'Carrier',
                value: data.carrier?.name,
              },
              {
                col: 3,
                colTablet: 3,
                title: 'Link to Carrier Website',
                value: data.carrier?.link && (
                  <Link url={data.carrier.link}>{data.carrier.link}</Link>
                ),
              },
              {
                col: 3,
                colTablet: 3,
                title: 'Tracking #',
                value: data.trackingNumber,
              },
            ],
            [
              {
                ...commonColProps,
                title: 'Date Shipped',
                value: formatOptionalDate(data.dateShipped),
              },
              {
                col: 3,
                colTablet: 3,
                title: 'Estimated Delivery Date',
                value: formatOptionalDate(data.estimatedDeliveryDate),
              },
              {
                ...commonColProps,
                title: 'LTL Invoice #',
                value: data.ltlInvoiceNumber,
              },
              {
                ...commonColProps,
                title: 'LTL Price',
                value: formatPrice(data.ltlPrice),
              },
            ],
          ],
        },
        {
          title: 'Products',
          children: (
            <List
              count={data.items.length}
              style={{ marginBottom: '32px' }}
              mainWrapperStyles={{ padding: 0 }}
              header={<ListHeader padding="8px" titles={titles} />}
            >
              {data.items.map((item) => (
                <ListCard
                  key={item.id}
                  headers={titles}
                  values={[
                    item.product.name,
                    item.count,
                    formatPrice(item.price),
                    formatPrice(item.count * item.price),
                  ]}
                />
              ))}

              <ListCard
                withPlaceholders={false}
                values={[
                  <h3>Total: </h3>,
                  '',
                  <h3>{formatPrice(data.amount)}</h3>,
                ]}
              />
            </List>
          ),
        },
        // Owner 2026-10-16: with an LTL split, each event's percent and
        // share of the LTL price; without one the linked events as before.
        data.eventAllocations?.length
          ? {
              title: 'LTL price by event',
              children: (
                <List
                  count={data.eventAllocations.length}
                  header={<ListHeader titles={splitTitles} />}
                >
                  {data.eventAllocations.map((row) => (
                    <ListCard
                      key={row.eventId}
                      headers={splitTitles}
                      values={[
                        row.event?.name ?? `Event ${row.eventId}`,
                        `${Number(row.percent)}%`,
                        formatPrice(row.amount),
                      ]}
                    />
                  ))}
                </List>
              ),
            }
          : {
              title: 'Events',
              children: (
                <List
                  count={data.events?.length ?? 0}
                  header={<ListHeader titles={['Name']} />}
                >
                  {data.events?.map((item) => (
                    <ListCard key={item.id} values={[item.name]} />
                  ))}
                </List>
              ),
            },
      ]}
    />
  );
};
